/**
 * ReturningHero — the signed-in-with-roadmap hero per Home v1 spec §3.4.
 *
 * Two rows inside one card:
 *   1. Top bar  — greeting + meta (archetype, week x/12) on left,
 *                 progress composite (n/total, %, bar) on right.
 *   2. Split panel — ThisWeekList (actionable) + PhaseMap (orienting).
 *
 * The design does NOT use the reserved 6px coral left-border here — that's
 * reserved for AI-output surfaces (Readiness Result) and the New-user hero's
 * marketing moment. The returning hero is a working tool, not a presentation.
 */

import React, { useMemo } from "react";
import {
  Box,
  LinearProgress,
  Stack,
  Typography,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  RoadmapResponse,
  RoadmapTaskCompletion,
} from "../../api/client";
import { useAuth } from "../../hooks/useAuth";
import ThisWeekList from "./ThisWeekList";
import PhaseMap from "./PhaseMap";

interface ReturningHeroProps {
  roadmap: RoadmapResponse;
  completions: RoadmapTaskCompletion[];
}

const ARCHETYPE_LABEL_KEYS: Record<string, string> = {
  fast_pivot: "ReturningHero.archetypeFastPivot",
  standard_build: "ReturningHero.archetypeStandardBuild",
  deep_rebuild: "ReturningHero.archetypeDeepRebuild",
  internal_first: "ReturningHero.archetypeInternalFirst",
  cash_bridge: "ReturningHero.archetypeCashBridge",
  staff_level_up: "ReturningHero.archetypeStaffLevelUp",
};

function greeting(t: TFunction, name: string | null | undefined) {
  const hour = new Date().getHours();
  const tod =
    hour < 12
      ? t("ReturningHero.goodMorning")
      : hour < 18
        ? t("ReturningHero.goodAfternoon")
        : t("ReturningHero.goodEvening");
  return name
    ? t("ReturningHero.greetingWithName", { greeting: tod, firstName: name.split(" ")[0] })
    : tod;
}

function currentWeek(createdAt: string): number {
  const start = new Date(createdAt).getTime();
  const weeks = Math.floor((Date.now() - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return Math.min(Math.max(weeks, 1), 12);
}

export default function ReturningHero({
  roadmap,
  completions,
}: ReturningHeroProps) {
  const theme = useTheme();
  const { t } = useTranslation("home");
  const { user } = useAuth();

  const { totalTasks, doneCount, pct } = useMemo(() => {
    const total = roadmap.phases.reduce(
      (sum, p) =>
        sum +
        (p.weeks ?? []).reduce((wSum, w) => wSum + (w.tasks ?? []).length, 0),
      0,
    );
    const done = completions.length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { totalTasks: total, doneCount: done, pct };
  }, [roadmap, completions]);

  const archetypeLabelKey = ARCHETYPE_LABEL_KEYS[roadmap.archetype.slug];
  const archetypeLabel = archetypeLabelKey
    ? t(archetypeLabelKey)
    : roadmap.archetype.name;
  const week = currentWeek(roadmap.created_at);

  return (
    <Box
      component="section"
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: "16px",
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
      }}
    >
      {/* Top bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ pb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Stack spacing={0.5}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 24, md: 28 },
              fontWeight: 500,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color: "text.primary",
              m: 0,
            }}
          >
            {greeting(t, user?.display_name)}
          </Typography>
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ fontSize: 12.5, color: "text.secondary" }}
          >
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "6px",
                bgcolor: alpha(theme.palette.secondary.main, 0.08),
                color: "secondary.main",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {archetypeLabel}
            </Box>
            <Box component="span">
              <Trans
                i18nKey="ReturningHero.weekOfTotal"
                t={t}
                values={{ week }}
                components={[
                  <Box
                    component="span"
                    key="week0"
                    sx={{
                      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  />,
                ]}
              />
            </Box>
          </Stack>
        </Stack>

        <Stack
          spacing={0.75}
          sx={{
            width: { xs: "100%", md: 280 },
            flexShrink: 0,
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography
              component="span"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {t("ReturningHero.roadmapProgressLabel")}
            </Typography>
            <Box
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                fontSize: 13,
                fontWeight: 500,
                color: "secondary.main",
              }}
            >
              {t("ReturningHero.progressStats", { done: doneCount, total: totalTasks, pct })}
            </Box>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={pct}
            aria-label={t("ReturningHero.roadmapCompletionAriaLabel")}
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: "action.hover",
              "& .MuiLinearProgress-bar": {
                bgcolor: "secondary.main",
                borderRadius: 3,
              },
            }}
          />
        </Stack>
      </Stack>

      {/* Split panel */}
      <Box
        sx={{
          pt: 3,
          display: "grid",
          gap: { xs: 3, md: 4 },
          gridTemplateColumns: { xs: "1fr", md: "1.2fr 1px 1fr" },
        }}
      >
        <ThisWeekList
          roadmapId={roadmap.id}
          phases={roadmap.phases}
          completions={completions}
        />
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" } }}
        />
        <PhaseMap phases={roadmap.phases} completions={completions} />
      </Box>
    </Box>
  );
}
